import { useEffect } from "react";

import { useTranslation } from "react-i18next";
import styled from "styled-components";
import invariant from "ts-invariant";

import type { Form, SelectedDate } from "contexts/CalendarContext";
import { useCalendarContext } from "contexts/CalendarContext";
import { useProfileContext } from "contexts/ProfileContext";
import { ButtonsGroup } from "shared/atoms/ButtonsGroup";
import { Spinner } from "shared/atoms/Spinner";

import type { SetSelectArgs } from "../Calendar/Calendar";

import { AvailabilityForm } from "./AvailabilityForm";
import { BookAppointmentForm } from "./BookAppointmentForm";
import { Header } from "./Header";

interface Props {
  children?: React.ReactNode;
  defaultServiceID: number | undefined;
  selectedDate?: SelectedDate | null;
  onCloseBookingForm: () => void;
  refetchAppointments?: () => void;
  setSelect?: (args: SetSelectArgs) => void;
}

export const FormsContainer: React.FC<Props> = ({
  children,
  defaultServiceID,
  selectedDate,
  onCloseBookingForm,
  refetchAppointments,
  setSelect,
}) => {
  const { t } = useTranslation();
  const { profile } = useProfileContext();
  const {
    activeForm,
    setActiveForm,
    refreshForm,
    setRefreshForm,
    selectedAppointment,
    selectedBookingInterval,
    selectedPatient,
  } = useCalendarContext();

  invariant(profile);

  useEffect(() => {
    return () => {
      setActiveForm(null);
    };
  }, []);

  useEffect(() => {
    if (refreshForm) {
      setTimeout(() => {
        setRefreshForm(false);
      }, 200);
    }
  }, [refreshForm]);

  const handleButtonGroupClick = (form: Form) => {
    setActiveForm(form);
  };

  const onClose = () => {
    onCloseBookingForm();
    setActiveForm(null);
  };

  if (!activeForm) {
    return <></>;
  }

  const renderForm = () => {
    switch (activeForm) {
      case "availability":
        return (
          <AvailabilityForm
            selectedBookingInterval={selectedBookingInterval}
            selectedDate={selectedDate}
            onClose={onCloseBookingForm}
            refetchAppointments={refetchAppointments}
            setSelect={setSelect}
          />
        );

      case "book_appointment":
        return (
          <BookAppointmentForm
            defaultServiceID={defaultServiceID}
            event={selectedAppointment}
            selectedDate={selectedDate}
            selectedPatient={selectedPatient}
            onClose={onCloseBookingForm}
            refetchAppointments={refetchAppointments}
            setSelect={setSelect}
          />
        );
      default:
        return <></>;
    }
  };

  const editMode = !!selectedAppointment || !!selectedBookingInterval;

  return (
    <Container>
      <Header
        header={
          <ButtonsGroup
            group={[
              {
                value: "book_appointment",
                label: t("common.appointment"),
                disabled: editMode && activeForm !== "book_appointment",
              },
              {
                value: "availability",
                label: t("booking.forms.availability"),
                disabled: editMode && activeForm !== "availability",
              },
            ]}
            active={activeForm}
            setActive={value => handleButtonGroupClick(value as Form)}
          />
        }
        onClose={onClose}
      />
      {!refreshForm ? (
        <FormContainer>
          {renderForm()}
          {children}
        </FormContainer>
      ) : (
        <Spinner />
      )}
    </Container>
  );
};

const Container = styled.section`
  display: flex;
  flex-direction: column;
  height: 100vh;
  width: 400px;
  max-width: 100%;
  background: #fff;
  position: sticky;
  top: 0;
  border-left: 1px solid ${props => props.theme.colors.redesign.db10};

  ${props => props.theme.belowBreakpoint} {
    height: auto;
    width: auto;
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    overflow: auto;
    z-index: 999;
  }
`;

const FormContainer = styled.div`
  display: flex;
  flex: 1;
  overflow: auto;

  ${props => props.theme.belowBreakpoint} {
    overflow: initial;
  }
`;
